// Flight config + per-section copy + UI fragments — the Vouch-specific data that
// drives the scroll-world engine. Ported 1:1 from the original assets/vouch.js.
import type { ScrollWorldConfig, ScrollWorldCta } from './scroll-engine';

// Flip once the Higgsfield clips exist under public/assets/vid/ (they do — Gate 3).
export const USE_CLIPS = true;

export const IDS = ['apron', 'snag', 'camo', 'stores', 'hangar', 'release', 'departure'] as const;

// Media URL lists — the single source of truth shared by buildConfig() (what the engine
// loads) and the preloader (what it warms into cache). Keep these in lockstep.
export const STILL_URLS: string[] = IDS.map((id) => 'assets/scenes/' + id + '.jpg');
export const DIVE_URLS: string[] = IDS.map((id) => 'assets/vid/' + id + '.mp4');
export const CONN_URLS: string[] = [1, 2, 3, 4, 5, 6].map((n) => 'assets/vid/conn' + n + '.mp4');
export const CLIP_URLS: string[] = [...DIVE_URLS, ...CONN_URLS];

interface CopyEntry {
  id: string;
  label: string;
  eyebrow: string;
  title: string;
  body: string;
  scroll?: number;
  linger?: number;
  cta?: ScrollWorldCta;
}

// ---- per-section overlay copy (STORYBOARD.md §4) --------------------------------------
const COPY: CopyEntry[] = [
  { id: 'apron', label: 'Touchdown', scroll: 1.5,
    eyebrow: 'LOG F-2214 · DXB→RUH', title: 'Every flight becomes a record.',
    body: 'Hours, cycles, and sectors captured at the source — signed, not scribbled.' },
  { id: 'snag', label: 'The snag',
    eyebrow: 'DEFECT D-014 · MEL C', title: 'A snag with a deadline.',
    body: 'Raised from the open log, deferred by the book — the due date computes itself.' },
  { id: 'camo', label: 'CAMO office',
    eyebrow: 'AD 2026-11-04 · REQ-031', title: 'Airworthiness, kept score.',
    body: 'One directive, resolved across the fleet — exposure you can see, not assume.' },
  { id: 'stores', label: 'The stores',
    eyebrow: 'BATCH · ISSUE · LEDGER', title: 'Parts that leave a trail.',
    body: 'Received, batched, issued against the job. Every movement writes a row.' },
  { id: 'hangar', label: 'The hangar',
    eyebrow: 'WO-1007', title: 'One order, whole story.',
    body: 'Defect and requirement, bundled and signed — name, license, stamp, time.' },
  { id: 'release', label: 'The release', scroll: 1.7, linger: 0.5,
    eyebrow: 'CRS-0093', title: 'The signature that means it.',
    body: 'Release to service — backed by everything you just flew through.' },
  { id: 'departure', label: 'Departure', scroll: 1.8, linger: 0.45,
    eyebrow: 'VOUCH · TECH LOG & CAMO', title: "If it happened, it's provable.",
    body: 'Walk the same trail in the live demo — three minutes, real screens.',
    cta: { primary: { label: 'Enter the demo', href: '#demo' },
           secondary: { label: 'Book a walkthrough', href: '#book' } } },
];

// ---- UI fragments (the "real component" bridge, brief §5). Indexes 0..5. --------------
export const FRAGMENTS: string[] = [
  '<span class="v-chip v-chip--open"><i class="v-dot"></i>OPEN</span><span class="v-mono">LOG F-2214</span>',
  '<span class="v-badge">DEFERRED · MEL C — due 19 Jul</span>',
  '<span class="v-matrix"><span class="v-cell v-cell--ok">COMPLIED</span><span class="v-cell v-cell--open">OPEN</span><span class="v-cell v-cell--open">OPEN</span><span class="v-cell v-cell--na">N/A</span></span>',
  '<span class="v-code">2026-07-12 14:02 · PN 3G-2241-7 · BATCH B-77 · ISSUED → WO-1007</span>',
  '<span class="v-code"><span class="v-tick">✓</span> signed — J. MERCER · LIC 66-4821 · 12 Jul 14:20 UTC</span>',
  '<span class="v-chip v-chip--crs"><span class="v-tick" style="color:#fff">✓</span> CRS ISSUED</span>',
];

export function buildConfig(): ScrollWorldConfig {
  const sections = COPY.map((c, i) => {
    const s: ScrollWorldConfig['sections'][number] = {
      id: c.id, label: c.label, still: STILL_URLS[i],
      accent: '#4970AE', eyebrow: c.eyebrow, title: c.title, body: c.body,
    };
    if (c.scroll) s.scroll = c.scroll;
    if (c.linger) s.linger = c.linger;
    if (c.cta) s.cta = c.cta;
    if (USE_CLIPS) s.clip = DIVE_URLS[i];
    return s;
  });
  const connectors = USE_CLIPS ? CONN_URLS : [];
  return {
    brand: { name: 'VOUCH', href: '#top' },
    nav: false,                 // route rail (right dots) is the nav; top bar stays slim
    atmosphere: true,
    diveScroll: 1.3, connScroll: 0.9,
    hint: 'scroll to fly in',
    sections,
    connectors,
  };
}
